import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TwoFactorMethodKind } from '../../services/twoFactorService';
import {
  BaleIcon,
  CARD_METHOD_ICON_CLASS,
  EmailMethodIcon,
  GoogleAuthenticatorIcon,
  TelegramIcon
} from './TwoFactorMethodIcons';

export type SelectableTwoFactorMethod = 'authenticator' | 'email' | 'telegram' | 'bale';

type MethodLabelT = (key: string) => string;

export function methodLabel(method: TwoFactorMethodKind, t?: MethodLabelT): string {
  if (t) {
    switch (method) {
      case 'authenticator':
        return t('security.methodAuthenticator');
      case 'email':
        return t('security.methodEmail');
      case 'telegram':
        return t('security.methodTelegram');
      case 'bale':
        return t('security.methodBale');
      default:
        return t('security.methodNone');
    }
  }
  switch (method) {
    case 'authenticator':
      return 'Google Authenticator';
    case 'email':
      return 'Email';
    case 'telegram':
      return 'Telegram';
    case 'bale':
      return 'Bale (Iranian social network)';
    default:
      return 'None';
  }
}

interface TwoFactorMethodTabsProps {
  selected: SelectableTwoFactorMethod;
  onSelect: (method: SelectableTwoFactorMethod) => void;
  emailAvailable: boolean;
  enabled: boolean;
  activeMethod: TwoFactorMethodKind;
}

const tabBase =
  'relative rounded-lg border px-2 py-2 sm:px-2.5 sm:py-2.5 text-[10px] sm:text-xs font-medium transition-colors flex flex-row items-center justify-start gap-2 min-h-[3.5rem] sm:min-h-[4rem] w-full text-start leading-tight';
const tabSelected =
  'border-teal-600 bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200 shadow-sm';
const tabIdle =
  'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:text-slate-200 dark:hover:bg-gray-800';

type TabDef = {
  id: SelectableTwoFactorMethod;
  label: React.ReactNode;
  icon: React.ReactNode;
};

export const TwoFactorMethodTabs: React.FC<TwoFactorMethodTabsProps> = ({
  selected,
  onSelect,
  emailAvailable,
  enabled,
  activeMethod
}) => {
  const { t } = useTranslation('settings');
  const iconClass = CARD_METHOD_ICON_CLASS;

  const tabs: TabDef[] = [
    {
      id: 'authenticator',
      label: t('security.methodAuthenticator'),
      icon: <GoogleAuthenticatorIcon className={iconClass} />
    },
    ...(emailAvailable
      ? [{ id: 'email' as const, label: t('security.methodEmail'), icon: <EmailMethodIcon className={iconClass} /> }]
      : []),
    { id: 'telegram', label: t('security.methodTelegram'), icon: <TelegramIcon className={iconClass} /> },
    {
      id: 'bale',
      label: (
        <span>
          {t('security.methodBaleShort')}
          <span className="mt-0.5 block text-[9px] font-normal leading-snug text-slate-500 sm:text-[10px] dark:text-slate-400">
            {t('security.methodBaleHint')}
          </span>
        </span>
      ),
      icon: <BaleIcon className={iconClass} />
    }
  ];

  return (
    <div
      className="grid grid-cols-2 gap-2"
      role="tablist"
      aria-label={t('security.tabListAria')}
      data-tour-id="quick-settings-security-methods"
    >
      {tabs.map((tab) => {
        const isActiveProtection = enabled && activeMethod === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected === tab.id ? 'true' : 'false'}
            onClick={() => onSelect(tab.id)}
            className={`${tabBase} ${selected === tab.id ? tabSelected : tabIdle} ${
              isActiveProtection ? 'pe-7' : ''
            }`}
          >
            {tab.icon}
            <span className="min-w-0 flex-1">{tab.label}</span>
            {isActiveProtection && (
              <span className="absolute top-1 end-1 rounded-full bg-emerald-600/15 px-1 py-px text-[8px] sm:text-[9px] font-semibold text-emerald-700 dark:text-emerald-300">
                {t('security.tabActiveBadge')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
